import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme as useMuiTheme,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarToday as CalendarIcon,
  DirectionsCar as VehicleIcon,
  Assignment as WorkOrderIcon,
  Devices as DeviceIcon,
  People as ClientIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  People as PeopleIcon,      // для пункта Users
  Security as SecurityIcon,  // для пункта Roles (опционально)
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useThemeContext } from '../../context/ThemeContext';
import MobileNav from './MobileNav';
import AssignmentIcon from '@mui/icons-material/Assignment';

const drawerWidth = 280;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: ['admin', 'dispatcher', 'mechanic', 'client'] },
  { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar', roles: ['admin', 'dispatcher', 'mechanic'] },
  { text: 'Work Orders', icon: <WorkOrderIcon />, path: '/workorders', roles: ['admin', 'dispatcher', 'mechanic'] },
  { text: 'Journal', icon: <AssignmentIcon />, path: '/logs', roles: ['admin', 'dispatcher', 'mechanic'] },
  { text: 'Devices', icon: <DeviceIcon />, path: '/devices', roles: ['admin', 'dispatcher'] },
  { text: 'Vehicles', icon: <VehicleIcon />, path: '/vehicles', roles: ['admin', 'dispatcher', 'client'] },
  { text: 'Clients', icon: <ClientIcon />, path: '/clients', roles: ['admin', 'dispatcher'] },
  { text: 'Users', icon: <PeopleIcon />, path: '/users', roles: ['admin'] },
  { text: 'Roles', icon: <SecurityIcon />, path: '/roles', roles: ['admin'] },
  //{ text: 'Profile', icon: <PersonIcon />, path: '/profile', roles: ['admin', 'dispatcher', 'mechanic', 'client'] },
  //{ text: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['admin', 'dispatcher', 'mechanic', 'client'] },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotifOpen = (event) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const userPermissions = user?.permissions || [];
  const filteredMenuItems = menuItems.filter(item =>
    item.roles.includes(user?.role) && (user?.role === 'admin' || userPermissions.includes(item.text.toLowerCase()))
  );

  const desktopDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ justifyContent: 'center', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          HelpDesk FMS
        </Typography>
      </Toolbar>
      <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
        <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 32 }}>
          {user?.fullName?.charAt(0) || 'U'}
        </Avatar>
        <Typography variant="subtitle1" fontWeight={600}>{user?.fullName}</Typography>
        <Typography variant="caption" color="textSecondary">{user?.role?.toUpperCase()}</Typography>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, px: 1, py: 1 }}>
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <IconButton
              key={item.text}
              onClick={() => navigate(item.path)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive ? 'primary.light' : 'transparent',
                color: isActive ? 'primary.contrastText' : 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
              <Typography variant="body2" fontWeight={isActive ? 600 : 400}>{item.text}</Typography>
            </IconButton>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` }, bgcolor: 'background.paper', color: 'text.primary', boxShadow: 1 }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>Welcome back, {user?.fullName?.split(' ')[0]}!</Typography>
          <Tooltip title={mode === 'light' ? 'Dark mode' : 'Light mode'}>
            <IconButton onClick={toggleTheme} sx={{ mr: 1 }}>{mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}</IconButton>
          </Tooltip>
          <Tooltip title="Notifications">
            <IconButton onClick={handleNotifOpen} sx={{ mr: 1 }}>
              <Badge badgeContent={3} color="error"><NotificationsIcon /></Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <IconButton onClick={handleMenuOpen}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{user?.fullName?.charAt(0)}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={notifAnchorEl} open={Boolean(notifAnchorEl)} onClose={handleNotifClose} PaperProps={{ sx: { width: 320, maxHeight: 400, borderRadius: 2 } }}>
            <MenuItem><Box><Typography variant="body2" fontWeight={500}>New Work Order Assigned</Typography><Typography variant="caption" color="textSecondary">You have a new work order for today</Typography></Box></MenuItem>
            <MenuItem><Box><Typography variant="body2" fontWeight={500}>Device Installation Complete</Typography><Typography variant="caption" color="textSecondary">GPS device installed on vehicle ABC-123</Typography></Box></MenuItem>
          </Menu>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { width: 200, mt: 1, borderRadius: 2 } }}>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}><PersonIcon sx={{ mr: 1 }} /> Profile</MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}><SettingsIcon sx={{ mr: 1 }} /> Settings</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}><LogoutIcon sx={{ mr: 1 }} /> Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <MobileNav open={mobileOpen} onClose={handleDrawerToggle} menuItems={filteredMenuItems} />

      <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' } }} open>
        {desktopDrawer}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;